<template>
	<Teleport v-if="showPdfPreview" to="body">
		<InvoicePreview
			ref="pdfDialogRef"
			class="supplier-pdf-preview"
			role="dialog"
			aria-modal="true"
			:aria-label="title || resolvedFilename || 'Supplier document preview'"
			tabindex="-1"
			:show-preview="show"
			:invoice-html="html"
			:pdf-url="url"
			:render-html-as-pdf="Boolean(html && !url)"
			:pdf-filename="resolvedFilename"
			@keydown.esc.prevent.stop="emit('close')"
			@keydown.tab="trapDialogFocus"
			@close-preview="emit('close')"
			@download-pdf="emit('download')"
		>
			<template #sidebar>
				<div class="supplier-preview-sidebar">
					<div>
						<p class="supplier-preview-eyebrow">Supplier document</p>
						<h3 class="supplier-preview-sidebar-title">{{ title || resolvedFilename }}</h3>
						<p v-if="resolvedFilename" class="supplier-preview-filename">{{ resolvedFilename }}</p>
					</div>
					<div class="supplier-preview-sidebar-actions">
						<button
							v-if="url"
							type="button"
							class="supplier-preview-action"
							@click="emit('open-original')"
						>
							Open original
						</button>
						<button
							type="button"
							class="supplier-preview-action supplier-preview-action--primary"
							@click="emit('download')"
						>
							Download
						</button>
					</div>
				</div>
			</template>
		</InvoicePreview>
	</Teleport>

	<Teleport v-else-if="show" to="body">
		<div class="supplier-preview-overlay" @click.self="emit('close')">
			<section
				ref="dialogRef"
				class="supplier-preview-dialog"
				role="dialog"
				aria-modal="true"
				:aria-labelledby="titleId"
				tabindex="-1"
				@keydown.esc.prevent.stop="emit('close')"
				@keydown.tab="trapDialogFocus"
			>
				<header class="supplier-preview-header">
					<div>
						<p class="supplier-preview-eyebrow">Supplier document</p>
						<h3 :id="titleId" class="supplier-preview-title">
							{{ title || resolvedFilename || 'Document preview' }}
						</h3>
						<p v-if="resolvedFilename" class="supplier-preview-filename">{{ resolvedFilename }}</p>
					</div>
					<button type="button" class="supplier-preview-close" @click="emit('close')">Close</button>
				</header>

				<div class="supplier-preview-body">
					<div v-if="loading" class="supplier-preview-state" role="status">
						Loading preview…
					</div>
					<div v-else-if="error" class="supplier-preview-state supplier-preview-state--error" role="alert">
						{{ error }}
					</div>
					<img
						v-else-if="previewKind === 'image' && url"
						class="supplier-preview-image"
						:src="url"
						:alt="title || resolvedFilename || 'Supplier document preview'"
					/>
					<iframe
						v-else-if="previewKind === 'html' && url"
						class="supplier-preview-html"
						:src="url"
						:title="`${title || resolvedFilename || 'Supplier document'} preview`"
						sandbox=""
						referrerpolicy="no-referrer"
					></iframe>
					<div v-else-if="previewKind === 'text'" class="supplier-preview-text-wrap">
						<div v-if="textLoading" class="supplier-preview-state" role="status">
							Loading text preview…
						</div>
						<div v-else-if="textError" class="supplier-preview-state supplier-preview-state--error" role="alert">
							{{ textError }}
						</div>
						<pre v-else class="supplier-preview-text">{{ textContent }}</pre>
					</div>
					<div v-else class="supplier-preview-state supplier-preview-state--unsupported">
						<strong>Preview is not available for this file type.</strong>
						<span>Use Open original or Download to view the document.</span>
					</div>
				</div>

				<footer class="supplier-preview-footer">
					<button type="button" class="supplier-preview-action" @click="emit('close')">Close</button>
					<button
						v-if="url"
						type="button"
						class="supplier-preview-action"
						@click="emit('open-original')"
					>
						Open original
					</button>
					<button
						type="button"
						class="supplier-preview-action supplier-preview-action--primary"
						:disabled="!url && !html"
						@click="emit('download')"
					>
						Download
					</button>
				</footer>
			</section>
		</div>
	</Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import InvoicePreview from '@/components/invoices/InvoicePreview.vue';

const props = defineProps({
	show: { type: Boolean, default: false },
	title: { type: String, default: '' },
	filename: { type: String, default: '' },
	mimeType: { type: String, default: '' },
	url: { type: String, default: '' },
	html: { type: String, default: '' },
	loading: { type: Boolean, default: false },
	error: { type: String, default: '' },
});

const emit = defineEmits(['close', 'open-original', 'download']);

const dialogRef = ref(null);
const pdfDialogRef = ref(null);
const textContent = ref('');
const textLoading = ref(false);
const textError = ref('');
const titleId = `supplier-document-preview-${Math.random().toString(36).slice(2, 9)}`;
let textRequestController = null;
let previousBodyOverflow = null;
let appRoot = null;
let appRootWasInert = false;

const resolvedFilename = computed(() => String(props.filename || 'document').trim() || 'document');

const extension = computed(() => {
	const name = resolvedFilename.value.toLowerCase();
	const dotIndex = name.lastIndexOf('.');
	return dotIndex >= 0 ? name.slice(dotIndex + 1) : '';
});

const previewKind = computed(() => {
	if (props.html) return 'pdf';
	const mime = String(props.mimeType || '').toLowerCase();
	if (mime === 'application/pdf' || extension.value === 'pdf') return 'pdf';
	if (mime.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension.value)) {
		return 'image';
	}
	if (mime === 'text/html' || ['html', 'htm'].includes(extension.value)) return 'html';
	if (mime.startsWith('text/') || ['txt', 'md', 'csv'].includes(extension.value)) {
		return 'text';
	}
	return 'unsupported';
});

const showPdfPreview = computed(
	() =>
		props.show &&
		previewKind.value === 'pdf' &&
		!props.loading &&
		!props.error &&
		Boolean(props.url || props.html),
);

const cancelTextRequest = () => {
	textRequestController?.abort?.();
	textRequestController = null;
};

const loadTextPreview = async () => {
	cancelTextRequest();
	textContent.value = '';
	textError.value = '';
	textLoading.value = false;
	if (!props.show || props.loading || props.error || previewKind.value !== 'text' || !props.url) {
		return;
	}

	const controller = typeof AbortController === 'function' ? new AbortController() : null;
	textRequestController = controller;
	textLoading.value = true;
	try {
		const response = await fetch(props.url, controller ? { signal: controller.signal } : undefined);
		if (!response?.ok) {
			throw new Error(`Preview request failed (${response?.status || 'unknown'})`);
		}
		const content = await response.text();
		if (textRequestController !== controller) return;
		textContent.value = content;
	} catch (error) {
		if (error?.name === 'AbortError' || textRequestController !== controller) return;
		textError.value = 'Unable to load the text preview.';
	} finally {
		if (textRequestController === controller) {
			textRequestController = null;
			textLoading.value = false;
		}
	}
};

watch(
	() => [props.show, props.url, props.mimeType, props.filename, props.loading, props.error],
	() => {
		void loadTextPreview();
	},
	{ immediate: true },
);

const activeDialogElement = () => pdfDialogRef.value?.$el || dialogRef.value || null;

const trapDialogFocus = (event) => {
	const dialog = activeDialogElement();
	if (!dialog) return;
	const focusable = Array.from(
		dialog.querySelectorAll(
			'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
		),
	);
	if (!focusable.length) {
		event.preventDefault();
		dialog.focus?.();
		return;
	}
	const first = focusable[0];
	const last = focusable[focusable.length - 1];
	if (document.activeElement === dialog) {
		event.preventDefault();
		(event.shiftKey ? last : first).focus?.();
	} else if (event.shiftKey && document.activeElement === first) {
		event.preventDefault();
		last.focus?.();
	} else if (!event.shiftKey && document.activeElement === last) {
		event.preventDefault();
		first.focus?.();
	}
};

const unlockModalEnvironment = () => {
	if (typeof document === 'undefined') return;
	if (previousBodyOverflow !== null) {
		document.body.style.overflow = previousBodyOverflow;
		previousBodyOverflow = null;
	}
	if (appRoot) {
		appRoot.inert = appRootWasInert;
		appRoot = null;
		appRootWasInert = false;
	}
};

watch(
	() => [props.show, showPdfPreview.value],
	([show]) => {
		if (typeof document === 'undefined') return;
		if (show) {
			if (previousBodyOverflow === null) {
				previousBodyOverflow = document.body.style.overflow;
				document.body.style.overflow = 'hidden';
			}
			if (!appRoot) {
				appRoot = document.getElementById('app');
				if (appRoot) {
					appRootWasInert = Boolean(appRoot.inert);
					appRoot.inert = true;
				}
			}
			void nextTick(() => activeDialogElement()?.focus?.());
			return;
		}
		unlockModalEnvironment();
	},
	{ immediate: true },
);

onBeforeUnmount(() => {
	cancelTextRequest();
	unlockModalEnvironment();
});
</script>

<style scoped>
.supplier-preview-overlay {
	position: fixed;
	inset: 0;
	z-index: 1000;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 16px;
	background: rgba(0, 0, 0, 0.78);
}

.supplier-preview-dialog {
	display: flex;
	flex-direction: column;
	width: min(960px, calc(100vw - 32px));
	height: min(760px, calc(100vh - 32px));
	min-height: 320px;
	background: #fff;
	box-shadow: 0 24px 70px rgba(15, 23, 42, 0.32);
}

.supplier-preview-header,
.supplier-preview-footer {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 14px 20px;
	border-color: var(--border-color, #e5e7eb);
	background: #fff;
}

.supplier-preview-header {
	justify-content: space-between;
	border-bottom: 1px solid var(--border-color, #e5e7eb);
}

.supplier-preview-footer {
	justify-content: flex-start;
	border-top: 1px solid var(--border-color, #e5e7eb);
}

.supplier-preview-eyebrow {
	margin: 0 0 3px;
	color: var(--text-color-secondary, #6b7280);
	font-size: 0.72rem;
	font-weight: 700;
	letter-spacing: 0.08em;
	text-transform: uppercase;
}

.supplier-preview-title,
.supplier-preview-sidebar-title {
	margin: 0;
	color: var(--text-color, #111827);
	font-family: 'Fraunces', Georgia, serif;
	font-size: 1.15rem;
}

.supplier-preview-filename {
	margin: 4px 0 0;
	color: var(--text-color-secondary, #6b7280);
	font-size: 0.78rem;
	word-break: break-word;
}

.supplier-preview-close,
.supplier-preview-action {
	min-height: 34px;
	padding: 7px 12px;
	border: 1px solid var(--border-color, #d1d5db);
	border-radius: 5px;
	background: #fff;
	color: var(--noc-blue, #0065ff);
	font: inherit;
	font-size: 0.84rem;
	font-weight: 600;
	cursor: pointer;
}

.supplier-preview-action--primary {
	border-color: var(--noc-blue, #0065ff);
	background: var(--noc-blue, #0065ff);
	color: #fff;
}

.supplier-preview-close:focus-visible,
.supplier-preview-action:focus-visible {
	outline: 2px solid rgba(0, 101, 255, 0.3);
	outline-offset: 2px;
}

.supplier-preview-action:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.supplier-preview-body {
	min-height: 0;
	flex: 1;
	overflow: auto;
	padding: 24px;
	background: #f3f4f6;
}

.supplier-preview-state {
	display: flex;
	min-height: 220px;
	align-items: center;
	justify-content: center;
	color: var(--text-color-secondary, #6b7280);
	text-align: center;
}

.supplier-preview-state--error {
	color: #b91c1c;
}

.supplier-preview-state--unsupported {
	flex-direction: column;
	gap: 8px;
}

.supplier-preview-image {
	display: block;
	max-width: 100%;
	max-height: 100%;
	margin: 0 auto;
	object-fit: contain;
	background: #fff;
	box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
}

.supplier-preview-html {
	display: block;
	width: 100%;
	height: 100%;
	min-height: 560px;
	border: 0;
	background: #fff;
	box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
}

.supplier-preview-text-wrap {
	min-height: 100%;
}

.supplier-preview-text {
	min-height: 100%;
	margin: 0;
	padding: 24px;
	overflow: auto;
	white-space: pre-wrap;
	word-break: break-word;
	background: #fff;
	color: #111827;
	font: 0.88rem/1.55 ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
	box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.supplier-preview-sidebar {
	display: flex;
	min-height: 100%;
	flex-direction: column;
	justify-content: space-between;
	gap: 24px;
}

.supplier-preview-sidebar-actions {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.supplier-pdf-preview :deep(.preview-footer .preview-btn--primary) {
	display: none;
}

@media (max-width: 640px) {
	.supplier-preview-dialog {
		width: 100%;
		height: 100%;
	}

	.supplier-preview-overlay {
		padding: 0;
	}

	.supplier-preview-header,
	.supplier-preview-footer {
		padding: 12px 14px;
	}

	.supplier-preview-body {
		padding: 12px;
	}
}
</style>
